import { Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useTranslation } from '@frontend/i18n';
import { Button, ButtonBar } from '@frontend/design-system';
import { AudienceTray } from '../audience-tray/audience-tray';
import { useAudienceTray } from '../providers/audience-tray-provider';
import { isSelectionValid } from '../utils/is-selection-valid';

type Props = {
  advanceCreateList: (newRecipients?: Recipient[]) => void;
  cancelCreateList: () => void;
  cancelEditList: () => void;
  finishEditList: () => Promise<void>;
};

export const RecipientsViewFooter = ({
  advanceCreateList,
  cancelCreateList,
  cancelEditList,
  finishEditList,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const { closeTray, initialRecipients, listToUpdate, mode, selectedRecipients, trackingIdPrefix, upsertAudience } =
    useAudienceTray();

  return (
    <AudienceTray.Footer hideBorder>
      <AudienceTray.Footer.ActionsBar css={{ marginTop: 0 }}>
        <ButtonBar css={{ justifyContent: 'flex-end', padding: 0 }}>
          {mode === 'recipients-select-list' || mode === 'recipients-edit-list' ? (
            <>
              {mode === 'recipients-select-list' && (
                <>
                  <Button
                    size='large'
                    variant='secondary'
                    onClick={cancelCreateList}
                    trackingId={`${trackingIdPrefix}-recipients-cancel-new-list-btn`}
                  >
                    {t('Cancel')}
                  </Button>
                  <Button
                    size='large'
                    onClick={() => advanceCreateList()}
                    disabled={!isSelectionValid(selectedRecipients, undefined, transformRecipient)}
                    trackingId={`${trackingIdPrefix}-recipients-advance-new-list-btn`}
                  >
                    {t('Save New List')}
                  </Button>
                </>
              )}
              {mode === 'recipients-edit-list' && (
                <>
                  <Button
                    size='large'
                    variant='secondary'
                    onClick={cancelEditList}
                    trackingId={`${trackingIdPrefix}-recipients-cancel-edit-list-btn`}
                  >
                    {t('Cancel')}
                  </Button>
                  <Button
                    size='large'
                    onClick={finishEditList}
                    disabled={!isSelectionValid(selectedRecipients, listToUpdate?.recipients, transformRecipient)}
                    trackingId={`${trackingIdPrefix}-recipients-finish-edit-list-btn`}
                  >
                    {t('Update List')}
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              <Button
                size='large'
                variant='secondary'
                onClick={closeTray}
                trackingId={`${trackingIdPrefix}-recipients-close-btn`}
              >
                {t('Close')}
              </Button>
              <Button
                size='large'
                onClick={upsertAudience}
                disabled={!isSelectionValid(selectedRecipients, initialRecipients, transformRecipient)}
                trackingId={`${trackingIdPrefix}-recipients-apply-btn`}
              >
                {initialRecipients.length > 0
                  ? t('Update Selection')
                  : t('Add Contact', { count: selectedRecipients.length })}
              </Button>
            </>
          )}
        </ButtonBar>
      </AudienceTray.Footer.ActionsBar>
    </AudienceTray.Footer>
  );
};

const transformRecipient = (recipient: Recipient) => recipient.destination;
